import React, { useState, useEffect } from 'react';
import { ArrowLeft, Scale, Trophy, HelpCircle, RotateCcw, Target } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';
import errorSound from '../assets/sounds/error2.mp3';

interface MagicScaleGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface Weight {
  id: number;
  value: number; // en grammes
  name: string;
  color: string;
  type: 'standard' | 'object';
}

interface Challenge {
  id: number;
  type: 'reach_target' | 'combine_weights' | 'compare_objects';
  targetMass: number;
  description: string;
  difficulty: number;
  tolerance: number;
}

const MagicScaleGame: React.FC<MagicScaleGameProps> = ({ mode, onReset, language }) => {
  const [level, setLevel] = useState(1);
  const [currentChallenge, setCurrentChallenge] = useState<Challenge | null>(null);
  const [leftPlatform, setLeftPlatform] = useState<Weight[]>([]);
  const [rightPlatform, setRightPlatform] = useState<Weight[]>([]);
  const [availableWeights, setAvailableWeights] = useState<Weight[]>([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [challengeCount, setChallengeCount] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playError] = useSound(errorSound, { volume: 0.3 });

  const tutorialSteps = [
    {
      title: language === 'en' ? 'Welcome to the Magic Scale!' : 'Bienvenue à la Balance Magique !',
      content: language === 'en' 
        ? 'Learn about mass and weight by using a magical balance! Place weights on the platforms to solve challenges.'
        : 'Apprenez les masses et les poids en utilisant une balance magique ! Placez des poids sur les plateaux pour résoudre les défis.'
    },
    {
      title: language === 'en' ? 'Understanding Mass' : 'Comprendre la masse',
      content: language === 'en'
        ? 'Mass is measured in grams (g) and kilograms (kg). 1 kilogram = 1000 grams. Watch the digital display!'
        : 'La masse se mesure en grammes (g) et kilogrammes (kg). 1 kilogramme = 1000 grammes. Observez l\'affichage digital !'
    },
    {
      title: language === 'en' ? 'Using the Balance' : 'Utiliser la balance',
      content: language === 'en'
        ? 'Drag weights onto the platforms. The balance will show you the total mass on each side. Try to reach the target!'
        : 'Glissez les poids sur les plateaux. La balance vous montrera la masse totale de chaque côté. Essayez d\'atteindre la cible !'
    }
  ];

  const standardWeights: Weight[] = [
    { id: 1, value: 1000, name: '1kg', color: '#DC2626', type: 'standard' },
    { id: 2, value: 500, name: '500g', color: '#EA580C', type: 'standard' },
    { id: 3, value: 200, name: '200g', color: '#D97706', type: 'standard' },
    { id: 4, value: 100, name: '100g', color: '#CA8A04', type: 'standard' },
    { id: 5, value: 50, name: '50g', color: '#65A30D', type: 'standard' },
    { id: 6, value: 20, name: '20g', color: '#059669', type: 'standard' },
  ];

  const dailyObjects: Weight[] = [
    { id: 10, value: 150, name: language === 'en' ? 'Apple' : 'Pomme', color: '#EF4444', type: 'object' },
    { id: 11, value: 250, name: language === 'en' ? 'Orange' : 'Orange', color: '#F97316', type: 'object' },
    { id: 12, value: 80, name: language === 'en' ? 'Egg' : 'Œuf', color: '#FEF3C7', type: 'object' },
    { id: 13, value: 30, name: language === 'en' ? 'Coin' : 'Pièce', color: '#FBBF24', type: 'object' },
    { id: 14, value: 500, name: language === 'en' ? 'Book' : 'Livre', color: '#3B82F6', type: 'object' },
    { id: 15, value: 120, name: language === 'en' ? 'Phone' : 'Téléphone', color: '#1F2937', type: 'object' },
  ];

  const generateChallenge = (): Challenge => {
    const challenges = {
      1: [ // Niveau 1 - Masses simples
        {
          id: 1,
          type: 'reach_target' as const,
          targetMass: 1000,
          description: language === 'en' ? 'Place exactly 1kg on the left platform' : 'Placez exactement 1kg sur le plateau de gauche',
          difficulty: 1,
          tolerance: 0
        },
        {
          id: 2,
          type: 'reach_target' as const,
          targetMass: 500,
          description: language === 'en' ? 'Place exactly 500g on the left platform' : 'Placez exactement 500g sur le plateau de gauche',
          difficulty: 1,
          tolerance: 0
        }
      ],
      2: [ // Niveau 2 - Combinaisons
        {
          id: 3,
          type: 'combine_weights' as const,
          targetMass: 750,
          description: language === 'en' ? 'Combine weights to make exactly 750g' : 'Combinez des poids pour faire exactement 750g',
          difficulty: 2,
          tolerance: 0
        },
        {
          id: 4,
          type: 'combine_weights' as const,
          targetMass: 320,
          description: language === 'en' ? 'Combine weights to make exactly 320g' : 'Combinez des poids pour faire exactement 320g',
          difficulty: 2,
          tolerance: 0
        }
      ],
      3: [ // Niveau 3 - Comparaisons d'objets
        {
          id: 5,
          type: 'compare_objects' as const,
          targetMass: 0,
          description: language === 'en' ? 'Sort objects by mass: lightest to heaviest' : 'Triez les objets par masse : du plus léger au plus lourd',
          difficulty: 3,
          tolerance: 0
        }
      ]
    };

    const levelChallenges = challenges[level as keyof typeof challenges];
    return levelChallenges[Math.floor(Math.random() * levelChallenges.length)];
  };

  const initializeWeights = () => {
    if (level <= 2) {
      setAvailableWeights([...standardWeights]);
    } else {
      setAvailableWeights([...standardWeights, ...dailyObjects]);
    }
  };

  const handleDragStart = (e: React.DragEvent, weight: Weight) => {
    e.dataTransfer.setData('application/json', JSON.stringify(weight));
  };

  const handleDrop = (e: React.DragEvent, platform: 'left' | 'right') => {
    e.preventDefault();
    
    const jsonData = e.dataTransfer.getData('application/json');
    
    // Check if the data is empty or invalid before parsing
    if (!jsonData || jsonData.trim() === '') {
      console.warn('No valid drag data received');
      return;
    }
    
    try {
      const weightData = JSON.parse(jsonData);
      const weight: Weight = { ...weightData, id: Date.now() }; // Nouvelle instance pour permettre multiples

      if (platform === 'left') {
        setLeftPlatform(prev => [...prev, weight]);
      } else {
        setRightPlatform(prev => [...prev, weight]);
      }
    } catch (error) {
      console.error('Error parsing drag data:', error);
      // Silently handle the error to prevent crashes
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeWeight = (platform: 'left' | 'right', index: number) => {
    if (platform === 'left') {
      setLeftPlatform(prev => prev.filter((_, i) => i !== index));
    } else {
      setRightPlatform(prev => prev.filter((_, i) => i !== index));
    }
  };

  const getTotalMass = (platform: Weight[]) => {
    return platform.reduce((total, weight) => total + weight.value, 0);
  };

  const formatMass = (mass: number) => {
    if (mass >= 1000) {
      return `${(mass / 1000).toFixed(mass % 1000 === 0 ? 0 : 1)}kg`;
    }
    return `${mass}g`;
  };

  const checkChallenge = () => {
    if (!currentChallenge) return;

    let correct = false;
    const leftMass = getTotalMass(leftPlatform);
    const rightMass = getTotalMass(rightPlatform);

    switch (currentChallenge.type) {
      case 'reach_target':
        correct = Math.abs(leftMass - currentChallenge.targetMass) <= currentChallenge.tolerance;
        break;
      case 'combine_weights':
        correct = Math.abs(leftMass - currentChallenge.targetMass) <= currentChallenge.tolerance;
        break;
      case 'compare_objects':
        // Pour la comparaison, vérifier que les objets sont triés correctement
        const sortedObjects = [...leftPlatform].sort((a, b) => a.value - b.value);
        correct = leftPlatform.every((weight, index) => weight.value === sortedObjects[index].value);
        break;
    }

    setIsCorrect(correct);
    setShowFeedback(true);

    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);

    if (correct) {
      const points = currentChallenge.difficulty * 10;
      setScore(score + points);
      playSuccess();
    } else {
      playError();
    }

    setTimeout(() => {
      setChallengeCount(challengeCount + 1);
      if (challengeCount >= 9) {
        setGameComplete(true);
        saveProgress(newStats);
      } else {
        if (challengeCount % 3 === 2 && level < 3) {
          setLevel(level + 1);
        }
        nextChallenge();
      }
    }, 2000);
  };

  const nextChallenge = () => {
    setCurrentChallenge(generateChallenge());
    setLeftPlatform([]);
    setRightPlatform([]);
    setShowFeedback(false);
    initializeWeights();
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'measures',
        mode: 'scale',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setLevel(1);
    setScore(0);
    setChallengeCount(0);
    setGameComplete(false);
    setShowTutorial(true);
    setTutorialStep(0);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
    nextChallenge();
  };

  const clearPlatforms = () => {
    setLeftPlatform([]);
    setRightPlatform([]);
  };

  useEffect(() => {
    if (!showTutorial) {
      initializeWeights();
      nextChallenge();
    }
  }, [showTutorial, level]);

  if (showTutorial) {
    return (
      <div className="w-full max-w-4xl">
        <div className="mb-6 flex items-center">
          <button
            onClick={onReset}
            className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
          </button>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Scale className="h-16 w-16 text-blue-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {tutorialSteps[tutorialStep].title}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
            {tutorialSteps[tutorialStep].content}
          </p>
          
          <div className="flex justify-center gap-4">
            {tutorialStep > 0 && (
              <button
                onClick={() => setTutorialStep(tutorialStep - 1)}
                className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                {language === 'en' ? 'Previous' : 'Précédent'}
              </button>
            )}
            
            {tutorialStep < tutorialSteps.length - 1 ? (
              <button
                onClick={() => setTutorialStep(tutorialStep + 1)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {language === 'en' ? 'Next' : 'Suivant'}
              </button>
            ) : (
              <button
                onClick={() => setShowTutorial(false)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {language === 'en' ? 'Start Weighing!' : 'Commencer à peser !'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <div className="text-lg font-medium text-blue-700 dark:text-blue-300">
            {language === 'en' ? `Level ${level}` : `Niveau ${level}`} - {language === 'en' ? `Challenge ${challengeCount + 1}/10` : `Défi ${challengeCount + 1}/10`}
          </div>
          
          <button
            onClick={() => setShowTutorial(true)}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        </div>
      </div>

      {gameComplete ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Scale Master!' : 'Maître de la Balance !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{score}</div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Total Points' : 'Points totaux'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {Math.round((stats.correctAnswers / stats.questionsAnswered) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Accuracy' : 'Précision'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {language === 'en' ? 'Weigh Again' : 'Peser à nouveau'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Challenge Panel */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {language === 'en' ? 'Challenge' : 'Défi'}
              </h3>
            </div>
            
            {currentChallenge && (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                  <p className="text-blue-800 dark:text-blue-200 font-medium mb-3">
                    {currentChallenge.description}
                  </p>
                  
                  {currentChallenge.targetMass > 0 && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                        {formatMass(currentChallenge.targetMass)}
                      </div>
                      <div className="text-sm text-blue-600 dark:text-blue-400">
                        {language === 'en' ? 'Target Mass' : 'Masse Cible'}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'en' ? 'Difficulty:' : 'Difficulté :'} 
                  <span className="ml-2">
                    {'⭐'.repeat(currentChallenge.difficulty)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Balance */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {language === 'en' ? 'Magic Balance' : 'Balance Magique'}
              </h3>
              <button
                onClick={clearPlatforms}
                className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>
            
            {/* Digital Display */}
            <div className="bg-black rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-green-400 font-mono text-lg">
                <div className="text-center">
                  <div className="text-sm mb-1">{language === 'en' ? 'LEFT' : 'GAUCHE'}</div>
                  <div className="text-2xl">{formatMass(getTotalMass(leftPlatform))}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm mb-1">{language === 'en' ? 'RIGHT' : 'DROITE'}</div>
                  <div className="text-2xl">{formatMass(getTotalMass(rightPlatform))}</div>
                </div>
              </div>
            </div>
            
            {/* Balance Visualization */}
            <div className="relative mb-6">
              {/* Balance Base */}
              <div className="w-full h-2 bg-slate-400 dark:bg-slate-600 rounded-full mb-4"></div>
              
              {/* Platforms */}
              <div className="grid grid-cols-2 gap-8">
                {/* Left Platform */}
                <div
                  onDrop={(e) => handleDrop(e, 'left')}
                  onDragOver={handleDragOver}
                  className="h-32 bg-slate-200 dark:bg-slate-700 rounded-lg border-2 border-dashed border-slate-400 dark:border-slate-500 flex flex-wrap items-center justify-center p-2 gap-1"
                >
                  {leftPlatform.map((weight, index) => (
                    <div
                      key={index}
                      onClick={() => removeWeight('left', index)}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white cursor-pointer hover:opacity-75"
                      style={{ backgroundColor: weight.color }}
                      title={weight.name}
                    >
                      {weight.type === 'standard' ? weight.name.replace(/[kg]/g, '') : weight.name.charAt(0)}
                    </div>
                  ))}
                </div>
                
                {/* Right Platform */}
                <div
                  onDrop={(e) => handleDrop(e, 'right')}
                  onDragOver={handleDragOver}
                  className="h-32 bg-slate-200 dark:bg-slate-700 rounded-lg border-2 border-dashed border-slate-400 dark:border-slate-500 flex flex-wrap items-center justify-center p-2 gap-1"
                >
                  {rightPlatform.map((weight, index) => (
                    <div
                      key={index}
                      onClick={() => removeWeight('right', index)}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white cursor-pointer hover:opacity-75"
                      style={{ backgroundColor: weight.color }}
                      title={weight.name}
                    >
                      {weight.type === 'standard' ? weight.name.replace(/[kg]/g, '') : weight.name.charAt(0)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <button
              onClick={checkChallenge}
              disabled={showFeedback}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {language === 'en' ? 'Check Balance' : 'Vérifier la balance'}
            </button>

            {showFeedback && (
              <div className={`mt-4 p-3 rounded-lg ${
                isCorrect 
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
              }`}>
                <p className="font-medium text-center">
                  {isCorrect 
                    ? (language === 'en' ? 'Perfect Balance! ⚖️' : 'Balance parfaite ! ⚖️')
                    : (language === 'en' ? 'Try Again! 🔄' : 'Réessayez ! 🔄')}
                </p>
              </div>
            )}
            
            <div className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
              {language === 'en' ? 'Score:' : 'Score :'} {score}
            </div>
          </div>

          {/* Available Weights */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {language === 'en' ? 'Available Weights' : 'Poids Disponibles'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'en' ? 'Standard Weights' : 'Poids Standards'}
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  {standardWeights.map((weight) => (
                    <div
                      key={weight.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, weight)}
                      className="p-3 rounded-lg cursor-move hover:opacity-75 transition-opacity text-white text-center font-bold"
                      style={{ backgroundColor: weight.color }}
                    >
                      <div className="text-sm">{weight.name}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {level >= 3 && (
                <div>
                  <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'en' ? 'Daily Objects' : 'Objets du Quotidien'}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {dailyObjects.map((weight) => (
                      <div
                        key={weight.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, weight)}
                        className="p-2 rounded-lg cursor-move hover:opacity-75 transition-opacity text-white text-center text-xs font-medium"
                        style={{ backgroundColor: weight.color }}
                      >
                        <div>{weight.name}</div>
                        <div className="text-xs opacity-75">{formatMass(weight.value)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                {language === 'en' ? 'Help:' : 'Aide :'}
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• {language === 'en' ? 'Drag weights onto the platforms' : 'Glissez les poids sur les plateaux'}</li>
                <li>• {language === 'en' ? 'Click weights to remove them' : 'Cliquez sur les poids pour les retirer'}</li>
                <li>• {language === 'en' ? '1kg = 1000g' : '1kg = 1000g'}</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MagicScaleGame;